import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { extractErrorMessage } from '../../core/utils/error-message-extractor.util';

/**
 * HTTP Error Interceptor
 *
 * Handles HTTP errors with user-friendly messages
 * Provides centralized error handling for all HTTP requests
 *
 * Error handling by status code:
 * - 401: Unauthorized - Authentication failed
 * - 403: Forbidden - Insufficient permissions
 * - 409: Conflict - Resource conflict
 * - 500: Internal Server Error
 * - 502: Bad Gateway
 * - 503: Service Unavailable
 * - 504: Gateway Timeout
 *
 * Usage in app.config.ts:
 * ```typescript
 * provideHttpClient(
 *   withInterceptors([
 *     ...PA_FE_BOILERPLATE_INTERCEPTORS,
 *     errorInterceptor
 *   ])
 * )
 * ```
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = 'An unexpected error occurred';

      if (error.error instanceof ErrorEvent) {
        // Client-side error
        errorMessage = `Error: ${error.error.message}`;
      } else {
        // Server-side error
        switch (error.status) {
          case 401:
            errorMessage = 'Authentication failed. Please log in again.';
            console.error('401 Unauthorized:', error.message);
            break;
          case 403:
            errorMessage =
              'Access forbidden. You do not have permission to access this resource.';
            console.error('403 Forbidden:', error.message);
            break;
          case 409:
            errorMessage =
              'Resource conflict. The operation could not be completed.';
            console.error('409 Conflict:', error.message);
            break;
          case 500:
            errorMessage = 'Internal server error. Please try again later.';
            console.error('500 Internal Server Error:', error.message);
            break;
          case 502:
            errorMessage =
              'Bad gateway. The server received an invalid response.';
            console.error('502 Bad Gateway:', error.message);
            break;
          case 503:
            errorMessage =
              'Service temporarily unavailable. Please try again later.';
            console.error('503 Service Unavailable:', error.message);
            break;
          case 504:
            errorMessage =
              'Gateway timeout. The request took too long to complete.';
            console.error('504 Gateway Timeout:', error.message);
            break;
          default:
            errorMessage = `Server error: ${error.status} - ${error.message}`;
            console.error(`HTTP Error ${error.status}:`, error.message);
        }
      }

      // Log full error details for debugging
      console.error('HTTP Error Details:', {
        status: error.status,
        message: error.message,
        url: error.url,
        error: error.error,
      });

      // Extract user-friendly error message using centralized utility
      // Supports RFC 7807, custom error codes, and FastAPI validation formats
      const userFriendlyMessage = extractErrorMessage(error, errorMessage);

      // Preserve the original error response body in a custom error object
      // This allows components to access detailed error information (e.g., RFC 7807)
      // while still providing a user-friendly message
      const enhancedError: any = new Error(errorMessage);
      enhancedError.status = error.status;
      enhancedError.error = error.error;
      enhancedError.url = error.url;
      enhancedError.originalError = error;
      enhancedError.userMessage = userFriendlyMessage; // Extracted user-friendly message

      return throwError(() => enhancedError);
    }),
  );
};
